import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Building2, Users, Link as LinkIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const CompaniesContacts = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">Companies & Contacts</h1>
          </div>
          <p className="text-lg text-slate-600">Manage your business relationships with comprehensive company and contact records.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Managing Companies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                Companies are organizations you want to track and engage with. Each company record stores comprehensive information including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Company name, industry, and size</li>
                <li>Location and website information</li>
                <li>Associated contacts and leads</li>
                <li>Technology stack and tools used</li>
                <li>Activity history and notes</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-slate-700">
                  <strong>Quick Tip:</strong> Companies are automatically created when you enrich leads or import data.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Managing Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                Contacts are individual people within companies. Track key information such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Name, job title, and role</li>
                <li>Email addresses and phone numbers</li>
                <li>Social media profiles (LinkedIn, Twitter)</li>
                <li>Communication history</li>
                <li>Related leads and opportunities</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-purple-600" />
                Relationships Between Records
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                I-Match CRM automatically links related records:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex gap-2">
                  <span className="font-semibold text-slate-900 w-24">Lead →</span>
                  <span className="text-slate-700">Associated with a Contact and Company</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-slate-900 w-24">Contact →</span>
                  <span className="text-slate-700">Belongs to a Company</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-semibold text-slate-900 w-24">Company →</span>
                  <span className="text-slate-700">Has multiple Contacts and Leads</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-600" />
                Adding & Editing Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">To Add a Company:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-slate-700 ml-4">
                    <li>Navigate to the Companies page</li>
                    <li>Click "Add Company" button</li>
                    <li>Fill in company details</li>
                    <li>Click "Save"</li>
                  </ol>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">To Add a Contact:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-slate-700 ml-4">
                    <li>Navigate to the Contacts page</li>
                    <li>Click "Add Contact" button</li>
                    <li>Select or create the associated company</li>
                    <li>Fill in contact information</li>
                    <li>Click "Save"</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompaniesContacts;
